// src/pages/Terms.tsx
import React from "react";
import SEOHead from "../components/SEOHead";
import PageTransition from "../components/PageTransition";
import AnimatedSection from "../components/AnimatedSection";
import GradientHero from '../components/GradientHero';
import { motion } from "framer-motion";
import {
  FileText, Shield, Link as LinkIcon, Globe, Lock, AlertTriangle,
  Scale, Mail, Building2, Cookie, RefreshCw, BookOpen
} from "lucide-react";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 }
  })
};

const lastUpdate = "26/09/2025"; // Europe/Paris

const Terms: React.FC = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Conditions générales d’utilisation",
    "url": "https://neoqualis.com/terms",
    "description": "CGU du site Neoqualis : accès, propriété intellectuelle, responsabilité, liens, sécurité, droit applicable."
  };

  return (
    <PageTransition>
      <SEOHead
        title="Conditions générales d’utilisation (CGU) | Neoqualis"
        description="CGU du site Neoqualis : règles d’usage du site, droits de propriété, responsabilités et dispositions légales."
        canonical="https://neoqualis.com/terms"
        jsonLd={jsonLd}
      />

      <div className="pt-16">
        {/* Hero */}
        <GradientHero
          title="Conditions générales d’utilisation"
		  subtitle={`Dernière mise à jour : ${lastUpdate}`}
        />

        {/* Intro */}
        <section className="section bg-white">
          <div className="container max-w-4xl">
            <AnimatedSection>
              <p className="text-gray-700">
                Les présentes Conditions générales d’utilisation (« <strong>CGU</strong> ») ont pour objet d’encadrer l’accès
                et l’utilisation du site <strong>https://neoqualis.com</strong> (le « <strong>Site</strong> ») édité par
                <strong> Neoqualis</strong>. En accédant au Site, vous acceptez sans réserve les CGU ci-dessous.
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* Sections */}
        <section className="section bg-gray-50">
          <div className="container max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Objet */}
              <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <FileText className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">1. Objet & opposabilité</h2>
                  </div>
                  <p className="text-gray-700">
                    Les CGU régissent vos relations avec Neoqualis concernant l’utilisation du Site. Elles peuvent être
                    modifiées à tout moment ; la version en ligne au jour de la consultation est la seule opposable.
                  </p>
                </div>
              </motion.div>

              {/* Accès */}
              <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Globe className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">2. Accès & disponibilité</h2>
                  </div>
                  <p className="text-gray-700">
                    Le Site est accessible 24/7, sauf interruption planifiée ou non, notamment pour maintenance ou cas de force
                    majeure. Neoqualis ne saurait être tenu responsable de toute indisponibilité.
                  </p>
                </div>
              </motion.div>

              {/* Propriété intellectuelle */}
              <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Shield className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">3. Propriété intellectuelle</h2>
                  </div>
                  <p className="text-gray-700">
                    L’ensemble des contenus du Site (textes, visuels, logos, chartes, maquettes, logiciels, codes) est protégé
                    par le droit d’auteur et/ou le droit des marques. Toute reproduction, représentation, adaptation ou
                    exploitation, totale ou partielle, sans autorisation écrite préalable est interdite.
                  </p>
                </div>
              </motion.div>

              {/* Liens */}
              <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <LinkIcon className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">4. Liens & sites tiers</h2>
                  </div>
                  <p className="text-gray-700">
                    Le Site peut contenir des liens vers des sites tiers. Neoqualis n’exerce aucun contrôle sur ces ressources
                    et décline toute responsabilité quant à leurs contenus, pratiques ou disponibilités.
                  </p>
                </div>
              </motion.div>

              {/* Comportements interdits */}
              <motion.div custom={4} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <AlertTriangle className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">5. Comportements interdits</h2>
                  </div>
                  <p className="text-gray-700">
                    Sont notamment interdits : l’extraction de données à grande échelle, toute tentative d’intrusion, la
                    perturbation du service, le contournement de mesures de sécurité, l’usage du Site en violation des lois.
                  </p>
                </div>
              </motion.div>

              {/* Responsabilité */}
              <motion.div custom={5} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Lock className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">6. Responsabilité</h2>
                  </div>
                  <p className="text-gray-700">
                    Le Site est fourni « en l’état ». Malgré le soin apporté à l’actualisation des informations, Neoqualis ne
                    garantit ni l’exhaustivité ni l’absence d’erreurs. En tout état de cause, Neoqualis ne pourra être tenu
                    responsable des dommages indirects, pertes d’exploitation, de profits ou de données.
                  </p>
                </div>
              </motion.div>

              {/* Données personnelles */}
              <motion.div custom={6} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <BookOpen className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">7. Données personnelles & cookies</h2>
                  </div>
                  <p className="text-gray-700">
                    Le traitement des données et l’usage des cookies sont décrits dans notre{" "}
                    <a className="text-primary underline" href="/privacy">Politique de confidentialité</a>. Par défaut,
                    le Site ne dépose aucun cookie non essentiel. En cas d’ajout de traceurs soumis au consentement, un bandeau
                    dédié vous permettra d’accepter ou de refuser.
                  </p>
                  <p className="text-gray-700 mt-3">
                    Pour des informations légales complémentaires (éditeur, hébergeur), consultez nos{" "}
                    <a className="text-primary underline" href="/legal">Mentions légales</a>.
                  </p>
                </div>
              </motion.div>

              {/* Sécurité */}
              <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Lock className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">8. Sécurité</h2>
                  </div>
                  <p className="text-gray-700">
                    Nous mettons en œuvre des mesures raisonnables pour protéger le Site (HTTPS, mises à jour, journaux de
                    sécurité). Aucun système n’étant infaillible, l’utilisateur s’engage à adopter un comportement prudent.
                  </p>
                </div>
              </motion.div>

              {/* Force majeure */}
              <motion.div custom={8} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <RefreshCw className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">9. Force majeure</h2>
                  </div>
                  <p className="text-gray-700">
                    Neoqualis ne saurait être tenu responsable en cas d’événement présentant les caractéristiques de la force
                    majeure au sens de la loi et de la jurisprudence françaises.
                  </p>
                </div>
              </motion.div>

              {/* Modifications */}
              <motion.div custom={9} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Cookie className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">10. Modification des CGU</h2>
                  </div>
                  <p className="text-gray-700">
                    Neoqualis peut modifier les CGU pour des raisons légales, techniques ou fonctionnelles. La date de dernière
                    mise à jour en haut de page fait foi. Une information pourra être affichée sur le Site en cas de changement
                    substantiel.
                  </p>
                </div>
              </motion.div>

              {/* Droit applicable */}
              <motion.div custom={10} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Scale className="text-primary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">11. Droit applicable & juridiction</h2>
                  </div>
                  <p className="text-gray-700">
                    Les CGU sont régies par le droit français. Sauf disposition d’ordre public contraire, tout litige relatif à
                    leur exécution ou interprétation relève de la compétence des tribunaux de <strong>Paris</strong>.
                  </p>
                </div>
              </motion.div>

              {/* Contact */}
              <motion.div custom={11} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Mail className="text-secondary w-7 h-7" />
                    </div>
                    <h2 className="text-xl font-semibold ml-4 text-gray-900">12. Contact</h2>
                  </div>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Neoqualis</strong></p>
                    <p className="flex items-center"><Building2 className="w-4 h-4 mr-2" /> 2-4 Rue Vincent Van Gogh, 93360 Neuilly-Plaisance — France</p>
                    <p className="flex items-center"><Mail className="w-4 h-4 mr-2" /> contact@neoqualis.com</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Terms;
